import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  findNavigableFormPath,
  resolveErrorDetailPlaceholders,
  type AnnotatedJsonSchema,
  type DomainEntity,
  type EntityDetailError,
} from '@web-config-app/core';
import {
  AdditionalValidationErrorKey,
  resolveLabelForFormPath,
  useConfigErrorBanner,
  useConfigUserFeedback,
  useEntityData,
} from '@web-config-app/core-react';
import { applyDefaultSchemaAnnotations } from '@web-config-app/schema-utils';
import {
  buildApiValidationErrorObjects,
  useEntityDetailsProps,
} from '@web-config-app/entity-form';

/**
 * Stable empty-array reference returned from the api-validation selector
 * when no errors are set. A fresh `[]` literal would fail Zustand's default
 * `Object.is` equality check on every unrelated store update and trigger
 * re-renders / re-derivations of `additionalFormErrors`.
 */
const EMPTY_API_VALIDATION_ERRORS: EntityDetailError[] = [];

interface UseResolvedConfigFeedbackInputs {
  domainEntity: DomainEntity;
  entityName: string;
  formPath: string | undefined;
  formSchema: AnnotatedJsonSchema | undefined;
}

/**
 * Resolves raw error/warning items emitted by `useEntityOperationError` and
 * `handleEventMessageResponse` into the fully-formatted shape the banner and
 * inline-error UI consume. Resolution lives at this layer because it requires
 * the entity-details schema + tree nodeMap that only the page provides.
 *
 * Owns the full chain:
 *  - Banner contents (ErrorBanner) — placeholders substituted, navigable path computed.
 *  - Inline `additionalErrors` for JSON Forms — same resolution + Ajv shape conversion.
 *  - `resolveBackendPointerFields` exposed for warning-message rendering, which lives
 *    in the page's JSX and can't be pre-resolved without duplicating the
 *    `configMessages` selector.
 */
export const useResolvedConfigFeedback = ({
  domainEntity,
  entityName,
  formPath,
  formSchema,
}: UseResolvedConfigFeedbackInputs) => {
  const { formatMessage } = useIntl();
  const entityRootData = useEntityData();
  const entityDetailsProps = useEntityDetailsProps();
  const entityTreeNodeMapFromProps = entityDetailsProps?.nodeMap;

  const schemaForLabels = React.useMemo<AnnotatedJsonSchema | undefined>(
    () =>
      applyDefaultSchemaAnnotations(domainEntity.schema).properties
        .attributes as AnnotatedJsonSchema | undefined,
    [domainEntity.schema],
  );

  // Read `entityRootData.attributes` lazily through a ref so the
  // resolver's identity stays stable across form edits. The resolver
  // only runs when there's an error/warning to render — by which point
  // the latest data is already in the ref. Capturing `attributes` in
  // the closure deps would re-fire downstream memos on every keystroke,
  // producing fresh array references that make JSON Forms dispatch
  // `UPDATE_CORE` and thrash the form's render cycle.
  const entityRootDataRef = React.useRef(entityRootData);
  entityRootDataRef.current = entityRootData;

  const resolveBackendPointerFields = React.useCallback(
    <T extends { detail?: string; text?: string; formPath?: string }>(
      item: T,
    ): T & { detail?: string; text?: string; path?: string } => {
      const { formPath: itemFormPath } = item;
      if (!itemFormPath) return item;
      const propertyLabel = resolveLabelForFormPath(itemFormPath, {
        schema: schemaForLabels,
        attributes: entityRootDataRef.current?.attributes,
        nodeMap: entityTreeNodeMapFromProps,
        formatMessage,
      });
      const path = findNavigableFormPath(
        itemFormPath,
        entityTreeNodeMapFromProps,
      );
      return {
        ...item,
        ...(item.detail !== undefined && {
          detail: resolveErrorDetailPlaceholders(item.detail, propertyLabel),
        }),
        ...(item.text !== undefined && {
          text: resolveErrorDetailPlaceholders(item.text, propertyLabel),
        }),
        path,
      };
    },
    [schemaForLabels, entityTreeNodeMapFromProps, formatMessage],
  );

  const {
    showErrorBanner,
    hideErrorBanner,
    errorBannerTitle,
    errorBannerContents,
  } = useConfigErrorBanner(entityName);

  // Server-error variants (which return `{ errorMessage,
  // secondaryErrorMessage }`) pass through unchanged; the list variant
  // gets each item resolved so banner consumers see substituted text
  // and a navigable path.
  const resolvedErrorBannerContents = React.useMemo(() => {
    if ('errorList' in errorBannerContents && errorBannerContents.errorList) {
      return {
        errorList: errorBannerContents.errorList.map(
          (error): EntityDetailError => resolveBackendPointerFields(error),
        ),
      };
    }
    return errorBannerContents;
  }, [errorBannerContents, resolveBackendPointerFields]);

  // Backend error pointers anchor at `/data/attributes/...` — they
  // never include the `domainEntityAttributes` wrapper that the
  // frontend layers on top of config-app-backend storage. JSON Forms
  // also sees instance paths relative to whatever data slice it was
  // handed, so matching against `/attributes/<formPath>` aligns with
  // both the backend pointer namespace and JSON Forms' internal one.
  const apiValidationErrors = useConfigUserFeedback(
    (state) =>
      state.additionalValidationErrors[
        AdditionalValidationErrorKey.ApiValidation
      ] ?? EMPTY_API_VALIDATION_ERRORS,
  );
  const slicePrefix = React.useMemo(() => {
    const basePrefix = '/attributes';
    if (!formPath || formPath === 'root') return basePrefix;
    return `${basePrefix}/${formPath.replace(/\./g, '/')}`;
  }, [formPath]);

  // Build the slice-relative ErrorObject[] passed to JsonForms via its
  // `additionalErrors` prop. Resolve placeholders here (same step as
  // the banner) so the inline message matches the banner copy.
  const additionalFormErrors = React.useMemo(
    () =>
      buildApiValidationErrorObjects(
        apiValidationErrors.map(resolveBackendPointerFields),
        slicePrefix,
        formSchema,
      ),
    [apiValidationErrors, resolveBackendPointerFields, slicePrefix, formSchema],
  );

  return {
    showErrorBanner,
    hideErrorBanner,
    errorBannerTitle,
    errorBannerContents: resolvedErrorBannerContents,
    additionalFormErrors,
    resolveBackendPointerFields,
    // Re-exposed for the audit-log container, which renders alongside
    // the form and needs the same `attributes`-rooted schema + nodeMap
    // this hook already computes for resolution.
    entityTreeNodeMap: entityTreeNodeMapFromProps,
    rootSchemaForLabels: schemaForLabels,
  };
};
